import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button, Offcanvas, Form, InputGroup, Pagination, Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config";
import AddBatchModal from "./addProductionBatch";
import Layout from "../../components/layouts/Main";
import { Link } from "react-router-dom";
import { FaEye, FaFilter, FaSearch, FaTimes, FaTrash } from "react-icons/fa";

const ProcessBatchList = () => {
  const [batches, setBatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter states
  const [filters, setFilters] = useState({
    status: "",
    batchNumber: "",
    dateFrom: "",
    dateTo: "",
    bomId: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch batches
  const fetchBatches = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/production-batches`);
      setBatches(res.data);
    } catch (err) {
      console.error("Error fetching batches:", err);
    }
  };

  // Apply filters with useMemo to prevent unnecessary re-renders
  const filteredBatches = useMemo(() => {
    return batches.filter((batch) => {
      const matchesStatus = !filters.status || batch.status === filters.status;
      const matchesBatch =
        !filters.batchNumber ||
        batch.batchNumber.toLowerCase().includes(filters.batchNumber.toLowerCase());
      const matchesBom =
        !filters.bomId ||
        (batch.bomId?.bomId &&
          batch.bomId.bomId.toLowerCase().includes(filters.bomId.toLowerCase()));
      const matchesSearch =
        !searchTerm ||
        batch.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (batch.bomId?.bomId &&
          batch.bomId.bomId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        batch.status.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesDate = true;
      if (filters.dateFrom || filters.dateTo) {
        const batchDate = new Date(batch.createdAt);
        if (filters.dateFrom)
          matchesDate = matchesDate && batchDate >= new Date(filters.dateFrom);
        if (filters.dateTo)
          matchesDate = matchesDate && batchDate <= new Date(filters.dateTo);
      }

      return matchesStatus && matchesBatch && matchesBom && matchesDate && matchesSearch;
    });
  }, [batches, filters, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredBatches.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBatches.slice(indexOfFirstItem, indexOfLastItem);

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: "",
      batchNumber: "",
      dateFrom: "",
      dateTo: "",
      bomId: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);

  const handleBatchAdded = () => {
    fetchBatches();
    handleModalClose();
  };

  const handleDeleteClick = (batch) => {
    setBatchToDelete(batch);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!batchToDelete) return;

    try {
      await axios.delete(`${config.API_BASE_URL}/production-batches/${batchToDelete._id}`);
      // Refresh the batches list
      fetchBatches();
      toast.success(`Batch "${batchToDelete.batchNumber}" has been deleted successfully.`);
      setShowDeleteModal(false);
      setBatchToDelete(null);
    } catch (error) {
      console.error("Error deleting batch:", error);
      toast.error(`Failed to delete batch: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBatchToDelete(null);
  };

  // Helper function to format input materials
  const formatInputMaterials = (inputs) => {
    if (!inputs || inputs.length === 0) return "—";

    // Group by material and sum quantities
    const materialMap = {};
    inputs.forEach(input => {
      const materialName = input.materialId?.productName || input.materialId?.name || "Unknown";
      const quantity = parseFloat(input.quantity || 0);
      const unit = input.unit || "";

      if (materialMap[materialName]) {
        materialMap[materialName].quantity += quantity;
      } else {
        materialMap[materialName] = { quantity, unit };
      }
    });

    return Object.entries(materialMap).map(([name, data]) => (
      <div key={name} className="mb-1">
        <small className="text-muted">{name}: {data.quantity} {data.unit}</small>
      </div>
    ));
  };

  // Helper function to format output (highest yield/90%)
  const formatOutput = (outputs) => {
    if (!outputs || outputs.length === 0) return "—";

    // Find the output with stored quantity (assuming highest yield means the one with stored quantity)
    const storedOutput = outputs.find(output => output.storedQuantity && output.storedQuantity > 0);

    if (storedOutput) {
      const productName = storedOutput.productId?.productName || storedOutput.productId?.name || "Unknown";
      return (
        <div>
          <small className="text-muted">{productName}</small>
          <br />
          <small className="text-success fw-bold">{storedOutput.storedQuantity} {storedOutput.unit || ""}</small>
        </div>
      );
    }

    return "—";
  };

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Dashboard</Link>
          </li>
          <li className="breadcrumb-item active">Production Batches</li>
        </ol>
      </nav>

      <Row className="gy-3">
        <Col className="col">
          <h2>Production Batches</h2>
        </Col>
        <Col className="text-end col-auto">
          <div className="d-flex justify-content-end flex-wrap gap-2">
            <Button variant="outline-secondary" onClick={() => setShowFilters(true)}>
              <FaFilter className="me-1" /> Filters
            </Button>
            <Button variant="primary" onClick={handleModalShow}>
              + Add Batch
            </Button>
          </div>
        </Col>
      </Row>

      {/* Search Bar */}
      <Row className="mt-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search batches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                <FaTimes />
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <small className="text-muted">
            Showing {filteredBatches.length} of {batches.length} batches
          </small>
        </Col>
      </Row>

      {/* Table */}
      <Row className="mt-4">
        <Col>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Batch #</th>
                  <th>BOM</th>
                  <th>Input Materials</th>
                  <th>Output</th>
                  <th>Status</th>
                  <th>Run Time (hrs)</th>
                  <th>Started At</th>
                  <th>Completed At</th>
                  <th>Created At</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((batch, i) => (
                    <tr key={batch._id}>
                      <td>{indexOfFirstItem + i + 1}</td>
                      <td>
                        <div>
                          <small className="fw-bold">{batch.label || "—"}</small>
                          <br />
                          <small className="text-muted">{batch.batchNumber || "—"}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <small className="fw-bold">{batch.bomId?.name || "—"}</small>
                          <br />
                          <small className="text-muted">{batch.bomId?.bomId || "—"}</small>
                        </div>
                      </td>
                      <td>{formatInputMaterials(batch.inputs)}</td>
                      <td>{formatOutput(batch.outputs)}</td>
                      <td>
                        <span
                          className={`badge text-bg-${batch.status === "completed"
                            ? "success"
                            : batch.status === "in-progress"
                              ? "warning"
                              : batch.status === "cancelled"
                                ? "danger"
                                : batch.status === "paused"
                                  ? "info"
                                  : "secondary"
                            }`}
                        >
                          {batch.status}
                        </span>
                      </td>
                      <td>
                        {batch.startedAt && batch.totalRunTimeInSeconds
                          ? (batch.totalRunTimeInSeconds / 3600).toFixed(2)
                          : "—"}
                      </td>
                      <td>
                        {batch.startedAt
                          ? new Date(batch.startedAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          : "—"}
                      </td>
                      <td>
                        {batch.completedAt
                          ? new Date(batch.completedAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          : "—"}
                      </td>
                      <td>
                        {new Date(batch.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="text-end">
                        <Link
                          to={`/batch/${batch._id}`}
                          className="btn btn-sm btn-outline-info"
                        >
                          <FaEye />
                        </Link>
                        {batch.status === "draft" && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="ms-2"
                            onClick={() => handleDeleteClick(batch)}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      No batches found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item
                    key={i + 1}
                    active={currentPage === i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              </Pagination>
            </div>
          )}
        </Col>
      </Row>

      <AddBatchModal
        show={showModal}
        handleClose={handleModalClose}
        refreshBatches={handleBatchAdded}
      />

      {/* Filter Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
        <Offcanvas.Header closeButton className="bg-light">
          <Offcanvas.Title>
            <FaFilter className="me-2" />
            Filter Batches
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="paused">Paused</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Batch Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter batch number"
                value={filters.batchNumber}
                onChange={(e) => setFilters({ ...filters, batchNumber: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>BOM ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter BOM ID"
                value={filters.bomId}
                onChange={(e) => setFilters({ ...filters, bomId: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date Range</Form.Label>
              <Row>
                <Col>
                  <Form.Control
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  />
                </Col>
              </Row>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="outline-secondary" onClick={clearFilters}>
                Clear All Filters
              </Button>
              <Button variant="primary" onClick={() => setShowFilters(false)}>
                Apply Filters
              </Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <FaTrash className="me-2" />
            Delete Batch
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaTrash size={48} className="text-danger mb-3" />
            <h5>Are you sure you want to delete this batch?</h5>
            <p className="text-muted mb-3">
              <strong>Batch Number:</strong> {batchToDelete?.batchNumber}
            </p>
            <div className="alert alert-warning">
              <strong>Warning:</strong> This action will:
              <ul className="text-start mt-2 mb-0">
                <li>Return all allocated materials back to inventory</li>
                <li>Reset any linked production requests to APPROVED status</li>
                <li>Permanently delete the batch record</li>
              </ul>
              <p className="mb-0 mt-2"><strong>This action cannot be undone!</strong></p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            <FaTrash className="me-2" />
            Delete Batch
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default ProcessBatchList;
